import { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { ProductsResponse, ProductInfo, VersionsManagerApi } from './types';
import { ResponseError } from '@backstage/errors';

export interface VersionsManagerClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
}

export class VersionsManagerClient implements VersionsManagerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: VersionsManagerClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getProducts(): Promise<ProductsResponse> {
    const response = await this.discoveryApi.getBaseUrl('versions-manager');
    const result = await this.fetchApi.fetch(`${response}/products`);

    if (!result.ok) {
      throw new Error(`Failed to fetch products: ${result.statusText}`);
    }

    const data = await result.json();
    return data;
  }

  async refreshExternalData(serviceType?: string): Promise<{
    message: string;
    services: number;
    products: number;
  }> {
    const response = await this.discoveryApi.getBaseUrl('versions-manager');
    const result = await this.fetchApi.fetch(`${response}/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: serviceType ? JSON.stringify({ serviceType }) : undefined,
    });

    if (!result.ok) {
      throw new Error(`Failed to refresh external data: ${result.statusText}`);
    }

    return await result.json();
  }
  async getProductById(id: string): Promise<ProductInfo> {
    const baseUrl = await this.discoveryApi.getBaseUrl('versions-manager');
    const res = await this.fetchApi.fetch(`${baseUrl}/products/${id}`);

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    const data: ProductInfo = await res.json();
    return data;
  }

  async updateProductData(id: string, data: any): Promise<void> {
    const baseUrl = await this.discoveryApi.getBaseUrl('versions-manager');
    const res = await this.fetchApi.fetch(`${baseUrl}/products/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }
  }

  async upgrade(upgradeData: any): Promise<{
    message: string;
    webhookResponse?: any;
  }> {
    const baseUrl = await this.discoveryApi.getBaseUrl('versions-manager');
    const res = await this.fetchApi.fetch(`${baseUrl}/upgrade`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(upgradeData),
    });

    if (!res.ok) {
      throw await ResponseError.fromResponse(res);
    }

    return await res.json();
  }
}
